import { votingIds } from "@artists/challenges-testkit"
import { CheckCircle } from "@mui/icons-material"
import { Button } from "@mui/material"
import { memo, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { EntryWithVote } from "../../../../api/hooks/vote"
import { VoteCommonCard } from "../../../@shared/vote-card"
import { useVoting } from "../vote-context"

interface Props {
	entry: EntryWithVote
	onUserAction(callback: () => Nullable<void>): boolean
	onPdpClick?: () => void
}

export const VoteCard = memo(({ entry, onUserAction, onPdpClick }: Props) => {
	const { t } = useTranslation("common")
	const texts = t("vote", { returnObjects: true })

	const { toggleVote, voted, unvoted } = useVoting()

	const isUnvoted = unvoted.has(entry.id)
	const isVoted = voted.has(entry.id)
	const isActuallySelected = isUnvoted
		? false
		: Boolean(isVoted || entry.isSelected)

	const onClick = useCallback(() => {
		const callback = () => toggleVote(entry, isActuallySelected)
		const canContinue = onUserAction(callback)

		if (canContinue) {
			callback()
		}
	}, [entry, isActuallySelected, onUserAction, toggleVote])

	const actionButton = useMemo(
		() => (
			<Button
				data-testid={votingIds.voteButton}
				startIcon={isActuallySelected ? <CheckCircle /> : null}
				sx={{
					mb: 2,
					width: "100%",
					textTransform: "none",
					"& svg": { fill: "white" },
				}}
				variant={isActuallySelected ? "contained" : "outlined"}
				onClick={onClick}
			>
				{isActuallySelected ? texts.selected : texts.vote}
			</Button>
		),
		[isActuallySelected, onClick, texts.selected, texts.vote],
	)

	return (
		<VoteCommonCard
			entry={entry}
			key={entry.designId}
			isSelected={isActuallySelected}
			actionButton={actionButton}
			onPdpClick={onPdpClick}
		/>
	)
})
